import { Checkbox, Dropdown, Field, Message, RadioGroup, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { getNumberOfSeparations, isPhaseEarlierThan } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { Phase, StateOfMaterial } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { ISieveSize } from 'Data/ViewModels/Equipment/ISieveSize';
import deepEquals from 'Lib/Object/deepEquals';
import { pipe } from 'ramda';
import React from 'react';
import { useSelector } from 'react-redux';

let SpecimenPreparationPageSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const availableSieveSizes = useSelector(
    pipe(createWorksheetSelector!('data.Equipment.AllSieveSizes', false), (sieveSize) =>
      sieveSize.filter((s: ISieveSize) => s.IsCommon)
    ),
    deepEquals
  );

  const nominalParticleSizeOptions = availableSieveSizes.map((s: ISieveSize) => ({ label: s.Caption, value: s.Id }));

  const dryingPhase = useSelector(createWorksheetSelector!('Preparation.DryingPhase')) as Phase;
  const washingPhase = useSelector(createWorksheetSelector!('Preparation.WashingPhase')) as Phase;
  const totalDryMassDeterminationBy = useSelector(
    createWorksheetSelector!('Preparation.TotalDryMassDeterminationBy')
  ) as StateOfMaterial;

  const numberOfSeparations = useSelector(pipe(createWorksheetSelector!('', false), getNumberOfSeparations));

  const materialDriedOptions: Array<{ label: string; value: Phase; disabled?: boolean }> = [
    { label: 'None (assumed dry)', value: Phase.Never },
    { label: 'Before separation', value: Phase.WholePortion },
    {
      label: 'After first separation (coarser assumed dry)',
      value: Phase.AfterFirstSeparation,
      disabled: numberOfSeparations < 1,
    },
    {
      label: 'After second separation (coarser assumed dry)',
      value: Phase.AfterSecondSeparation,
      disabled: numberOfSeparations < 2,
    },
  ];

  const materialWashedOptions: Array<{ label: string; value: Phase; disabled?: boolean }> = [
    { label: 'None', value: Phase.Never },
    {
      label: 'Whole specimen',
      value: Phase.WholePortion,
      disabled: isPhaseEarlierThan(Phase.WholePortion, dryingPhase),
    },
    {
      label: 'After first separation',
      value: Phase.AfterFirstSeparation,
      disabled: numberOfSeparations < 1 || isPhaseEarlierThan(Phase.AfterFirstSeparation, dryingPhase),
    },
    {
      label: 'After second separation',
      value: Phase.AfterSecondSeparation,
      disabled: numberOfSeparations < 2 || isPhaseEarlierThan(Phase.AfterSecondSeparation, dryingPhase),
    },
    {
      label: 'After third separation',
      value: Phase.AfterThirdSeparation,
      disabled: numberOfSeparations < 3 || isPhaseEarlierThan(Phase.AfterThirdSeparation, dryingPhase),
    },
  ];

  var totalDryMassDeterminationByOptions: Array<{ label: string; value: StateOfMaterial; disabled?: boolean }> = [
    { label: 'Oven drying', value: StateOfMaterial.OvenDry },
    { label: 'Using related water content', value: StateOfMaterial.Moist },
  ];

  var totalDryMassDeterminationByIsEditable =
    dryingPhase === washingPhase &&
    [Phase.WholePortion, Phase.AfterFirstSeparation, Phase.AfterSecondSeparation].includes(dryingPhase);

  return (
    <PageSection heading="Specimen Preparation">
      <NamePrefixModifier appendNamePrefix="Preparation">
        <ListFieldSection>
          <Field
            input={<Dropdown />}
            name="VisualNominalParticleSize"
            options={nominalParticleSizeOptions}
            simpleValue
          />
          <Field input={<Checkbox />} name="SampleContainsFineMaterial" />
          <Field
            input={<RadioGroup />}
            name="DryingPhase"
            options={materialDriedOptions}
            confirm={dryingPhase !== Phase.Never}
            confirmTitle="Confirm Drying Selection"
            confirmDialogWidth="550px"
            confirmText={(newDryingPhase: Phase) => (
              <>
                <div>
                  <Message border={false} info>
                    {`Previous drying selection: ${materialDriedOptions.find((o) => o.value === dryingPhase)?.label}`}
                  </Message>
                </div>
                <div>
                  <Message border={false} info>
                    {`Previous washing selection: ${materialWashedOptions.find((o) => o.value === washingPhase)
                      ?.label}`}
                  </Message>
                </div>
                <br />
                <div>
                  <Message border={false} info>
                    {`New drying selection: ${materialDriedOptions.find((o) => o.value === newDryingPhase)?.label}`}
                  </Message>
                </div>
                <br />
                <div>
                  <Message border={false} error>
                    Changing the drying selection will clear data entered for dry mass measurements.
                  </Message>
                </div>
                <br />
                {isPhaseEarlierThan(washingPhase, newDryingPhase) ? (
                  <>
                    <div>
                      <Message border={false} error>
                        Changing the drying selection will make the previous washing selection invalid and clear data
                        entered for the previous washing.
                      </Message>
                    </div>
                    <br />
                  </>
                ) : null}
                <div>Continue?</div>
              </>
            )}
          />
          <Field
            input={<RadioGroup />}
            name="WashingPhase"
            options={materialWashedOptions}
            confirm={washingPhase !== Phase.Never}
            confirmTitle="Confirm Washing Selection"
            confirmDialogWidth="550px"
            confirmText={(newWashingPhase: Phase) => (
              <>
                <div>
                  <Message border={false} info>
                    {`Previous washing selection: ${materialWashedOptions.find((o) => o.value === washingPhase)
                      ?.label}`}
                  </Message>
                </div>
                <br />
                <div>
                  <Message border={false} info>
                    {`New washing selection: ${materialWashedOptions.find((o) => o.value === newWashingPhase)?.label}`}
                  </Message>
                </div>
                <br />
                <div>
                  <Message border={false} error>
                    {'Changing the washing selection will clear data entered for the previous washing.'}
                  </Message>
                </div>
                <br />
                <div>Continue?</div>
              </>
            )}
          />
          {totalDryMassDeterminationByIsEditable ? (
            <Field
              input={<RadioGroup />}
              name="TotalDryMassDeterminationBy"
              options={totalDryMassDeterminationByOptions}
              confirm={true}
              confirmTitle="Confirm Total Dry Mass Determination By"
              confirmText="Changing the Total Dry Mass Determination By after data entry has begun can lead to data loss. This cannot be undone, continue?"
            />
          ) : (
            <Field
              input={<TextOutput />}
              name="TotalDryMassDeterminationBy"
              value={totalDryMassDeterminationByOptions.find((x) => x.value === totalDryMassDeterminationBy)?.label}
            />
          )}
        </ListFieldSection>
      </NamePrefixModifier>
    </PageSection>
  );
};

SpecimenPreparationPageSection = withInjectedProps(SpecimenPreparationPageSection);

export { SpecimenPreparationPageSection };
export default SpecimenPreparationPageSection;
